/*
  Guide des tailles Kloody : tiroir modal (élément <dialog>).
  - Ouverture : tout élément [data-size-guide-open="<id du tiroir>"].
  - Fermeture : bouton [data-size-guide-close], touche Échap, clic sur le fond assombri.
  - Focus piégé dans le tiroir, rendu au lien d'origine à la fermeture.
  - Défilement de la page bloqué pendant l'ouverture.
  Aucune dépendance, aucun appel réseau : le contenu est déjà dans la page.
*/

const LOCK_CLASS = 'kl-size-guide-lock';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function lockScroll() {
  const root = document.documentElement;
  const scrollbar = window.innerWidth - root.clientWidth;
  root.style.setProperty('--kl-sg-scrollbar', `${Math.max(scrollbar, 0)}px`);
  root.classList.add(LOCK_CLASS);
}

function unlockScroll() {
  const root = document.documentElement;
  root.classList.remove(LOCK_CLASS);
  root.style.removeProperty('--kl-sg-scrollbar');
}

class KlSizeGuide extends HTMLElement {
  connectedCallback() {
    this._dialog = this.querySelector('dialog');
    if (!this._dialog) return;
    this._closing = false;
    this._pressedOnBackdrop = false;
    this._timer = 0;

    this._onPointerDown = (event) => {
      this._pressedOnBackdrop = event.target === this._dialog;
    };
    this._onClick = (event) => {
      if (event.target === this._dialog) {
        // Clic réel sur le fond (pas une sélection de texte qui finit hors du panneau)
        if (this._pressedOnBackdrop) this.close();
      } else if (event.target instanceof Element && event.target.closest('[data-size-guide-close]')) {
        this.close();
      }
      this._pressedOnBackdrop = false;
    };
    this._onCancel = (event) => {
      // Échap : on garde la main pour jouer l'animation de sortie
      event.preventDefault();
      this.close();
    };
    this._onClose = () => this._afterClose();
    this._onKeydown = (event) => this._trapFocus(event);

    this._dialog.addEventListener('pointerdown', this._onPointerDown);
    this._dialog.addEventListener('click', this._onClick);
    this._dialog.addEventListener('cancel', this._onCancel);
    this._dialog.addEventListener('close', this._onClose);
    this._dialog.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
    if (!this._dialog) return;
    this._dialog.removeEventListener('pointerdown', this._onPointerDown);
    this._dialog.removeEventListener('click', this._onClick);
    this._dialog.removeEventListener('cancel', this._onCancel);
    this._dialog.removeEventListener('close', this._onClose);
    this._dialog.removeEventListener('keydown', this._onKeydown);
    if (this._dialog.open) unlockScroll();
  }

  open(trigger) {
    const dialog = this._dialog;
    if (!dialog || dialog.open) return;
    clearTimeout(this._timer);
    this._closing = false;
    this._trigger = trigger || document.activeElement;
    dialog.showModal();
    lockScroll();
    const body = dialog.querySelector('.size-guide__body');
    if (body) body.scrollTop = 0;
    // Deux images d'attente : le navigateur doit peindre l'état fermé avant la transition
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
  }

  close() {
    const dialog = this._dialog;
    if (!dialog || !dialog.open || this._closing) return;
    this._closing = true;
    dialog.classList.remove('is-open');

    const panel = dialog.querySelector('.size-guide__panel');
    const finish = () => {
      clearTimeout(this._timer);
      if (panel) panel.removeEventListener('transitionend', onEnd);
      if (dialog.open) dialog.close();
      unlockScroll();
    };
    // On attend la fin du glissement (ou du fondu si l'animation est réduite par le système)
    const onEnd = (event) => {
      if (event.target === panel && (event.propertyName === 'transform' || event.propertyName === 'opacity')) finish();
    };

    if (!panel) {
      finish();
      return;
    }
    panel.addEventListener('transitionend', onEnd);
    this._timer = setTimeout(finish, 1300);
  }

  _afterClose() {
    this._closing = false;
    this._dialog.classList.remove('is-open');
    unlockScroll();
    let target = this._trigger;
    if (!target || !target.isConnected) {
      target = this.id ? document.querySelector(`[data-size-guide-open="${this.id}"]`) : null;
    }
    if (target) target.focus({ preventScroll: true });
    this._trigger = null;
  }

  _trapFocus(event) {
    if (event.key !== 'Tab') return;
    const dialog = this._dialog;
    const items = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const outside = !dialog.contains(active) || active === dialog;
    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('kl-size-guide')) {
  customElements.define('kl-size-guide', KlSizeGuide);

  // Délégation : les liens survivent aux rechargements de section (changement de variante)
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-size-guide-open]');
    if (!trigger) return;
    const guide = document.getElementById(trigger.getAttribute('data-size-guide-open'));
    if (guide instanceof KlSizeGuide) {
      event.preventDefault();
      guide.open(trigger);
    }
  });
}
